<template>
  <Form
    :validation-schema="schema"
    @submit="submit"
    v-slot="{ errors }"
    ref="form"
    action="/cart-details/"
    method="post"
    name="form_item"
    class="p-the-c01-form"
  >
    <div v-if="options.length > 0">
      <template v-for="(option, key) in optionTypes">
        <div
          v-if="isHiddenItem == false"
          :key="key"
          class="p-the-c01-form__item"
        >
          <label for="size" class="p-the-c01-form__item__label">
            {{ option.name }}
          </label>
          <div class="p-the-c01-form__item__select">
            <Field
              :name="`option_${key}`"
              as="select"
              v-model="flag[key]"
              :rules="{ required: true }"
            >
              <InputSelect
                :id="'size' + key"
                :name="'size' + key"
                color="#616161"
                :error="errors[`option_${key}`]"
                :options="getOptions(option.id)"
                @selected="onChangeSelect($event, option.id, option.name)"
              />
            </Field>
          </div>
        </div>
      </template>
    </div>

    <div v-if="clientList.length > 0">
      <div class="p-the-c01-form__item">
        <label
          style="font-size:10px"
          for="size"
          class="p-the-c01-form__item__label"
        >
          お客様番号
        </label>
        <div class="p-the-c01-form__item__select">
          <Field
            name="clientListOption"
            as="select"
            v-model="clientListOption"
            :rules="{ required: true }"
          >
            <InputSelect
              :name="'clientOption'"
              color="#616161"
              :error="errors.clientListOption"
              :options="clientList"
            />
          </Field>
        </div>
      </div>
    </div>

    <div class="p-the-c01-form__item">
      <label for="count" class="p-the-c01-form__item__label">個数</label>
      <FormCount
        id="count"
        v-model="count"
        :stock="stock"
        name="count"
        :min="1"
        :max="maxStock"
      />
    </div>

    <template v-if="haveExtraProps">
      <div class="p-the-c01-form__extras">
        <template v-if="sortedExtraOptions">
          <div v-for="(option, key) in sortedExtraOptions" :key="key">
            <Field
              :name="`extra_${key}`"
              :rules="{
                required: true,
                regex: new RegExp(option.regex),
                min: option.min_length,
                max: option.max_length
              }"
              v-model="option.value"
            >
              <C01ItemFormExtraProp
                :id="option.sku"
                :label="option.name"
                :placeholder="option.name"
                :maxlength="option.max_length"
                :minlength="option.min_length"
                :error="errors[`extra_${key}`]"
                class="p-the-c01-form__extras__item"
              />
              <ValidationErrorText v-show="errors[`extra_${key}`]">
                {{ errors[`extra_${key}`] }}
              </ValidationErrorText>
            </Field>
          </div>
        </template>
      </div>
    </template>

    <!-- 以下、元のテンプレートと同様に保持 -->
    <TheC01ItemFormNotePanelShortage
      v-if="(!userPointsAreGreaterThanProductPoint && hasStock) || !compareSaleEndDate"
      :possession="possession"
      :credit-card-enabled="creditCardEnabled"
      :cart-empty="cartEmpty"
      :count="count"
      :type="type"
      :sale-end-date="saleEndDate"
      class="p-the-c01-form__note"
    />

    <template v-if="!isOnlyTypeInCart">
      <TheC01ItemFormNotePanelGiftCard
        v-if="userPointsAreGreaterThanProductPoint && !cartEmpty && hasStock"
        :is-gift-card-and-normal-item-in-cart="isGiftCardAndNormalItemInCart"
        :is-normal-item-and-gift-card-in-cart="isNormalItemAndGiftCardInCart"
        :is-web-coupon-and-gift-item-in-cart="isWebCouponAndGiftItemInCart"
        :is-gift-card-and-web-coupon-item-in-cart="isGiftCardAndWebCouponItemInCart"
        :is-web-coupon-and-normal-item-in-cart="isWebCouponAndNormalItemInCart"
        :is-benepo-exchange-and-somethings-in-cart="isBenepoExchangeAndSomethingsInCart"
        class="p-the-c01-form__note"
      />
    </template>

    <TheC01ItemFormNotePanelDrink
      v-if="ageAlert"
      :value="isAdult"
      class="p-the-c01-form__note"
      @checked="onCheckAdult"
    />

    <TheC01ItemFormOnlyTypeInCart
      v-if="isOnlyTypeInCart"
      class="p-the-c01-form__note"
    />

    <div class="p-the-c01-form__button_group">
      <template v-if="hasStock">
        <div class="p-the-c01-form__button_group__item">
          <ButtonMain
            type="submit"
            :disabled="disabled || !compareSaleEndDate || isHiddenItem"
          >
            <template #icon>
              <IconCart :icon-size="40" icon-color="#ffffff" />
            </template>
            カートに追加
          </ButtonMain>
        </div>
      </template>
      <div class="p-the-c01-form__button_group__item">
        <ButtonMain
          type="button"
          kind="cancel"
          :disabled="isDrinkStop || !isSkuSelected || !compareSaleEndDate"
          @click-button="changeFavorite"
        >
          <template #icon>
            <IconBase :width="40" :height="40" view-box="0 0 40 40">
              <IconFavorite40px :is-favorite="isFavorite" />
            </IconBase>
          </template>
          {{ favoriteLabel }}
        </ButtonMain>
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import { mapState } from "vuex";
import FormCount from "@/common/components/organisms/FormCount.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import InputSelect from "@/common/components/atoms/InputSelect.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconFavorite40px from "@/common/components/icons/IconFavorite40px.vue";
import IconCart from "@/common/components/icons/IconCart.vue";
import TheC01ItemFormNotePanelShortage from "@/general/components/pages/parts/ProductDetails/TheC01ItemFormNotePanelShortage.vue";
import TheC01ItemFormNotePanelDrink from "@/general/components/pages/parts/ProductDetails/TheC01ItemFormNotePanelDrink.vue";
import TheC01ItemFormNotePanelGiftCard from "@/general/components/pages/parts/ProductDetails/TheC01ItemFormNotePanelGiftCard.vue";
import TheC01ItemFormOnlyTypeInCart from "@/general/components/pages/parts/ProductDetails/TheC01ItemFormOnlyTypeInCart.vue";
import C01ItemFormExtraProp from "@/general/components/pages/parts/ProductDetails/C01ItemFormExtraProp.vue";

import { Options, Vue } from "vue-class-component";
import * as yup from "yup";
import items from "@/general/store/modules/items";
import TheHeaderUserNavi from "@/common/components/organisms/header/TheHeaderUserNavi.vue";
import {
  getShoppingCartItems,
  patchItemfromShoppingcart,
  postBrowserHistory,
  postShoppingCartItems
} from "@/general/store/api";
import {
  PatchedShoppingCartItemRequest,
  ShoppingCartItemRequest
} from "gen/ricapi/api";
import { BrowsingHistoryRequest } from "gen/rssapi/api";

import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";
import LocalStorage from "@/general/utils/storage";

@Options({
  props: {
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    optionTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    isFavorite: {
      type: Boolean,
      required: true,
      default: false
    },
    point: {
      type: Number,
      required: true,
      default: 0
    },
    stock: {
      type: Number,
      required: true,
      default: 0
    },
    jan_code: {
      type: String,
      required: true,
      default: ""
    },
    type: {
      type: String,
      required: true,
      default: "normal"
    },
    extraProp: {
      type: [Array, Object],
      required: false,
      default: () => ({})
    },
    cartItem: {
      type: Array,
      required: false,
      default: () => []
    },
    possession: {
      type: Number,
      required: true,
      default: 0
    },
    creditCardEnabled: {
      type: Boolean,
      required: false
    },
    ageAlert: {
      type: Boolean,
      required: true
    },
    id: {
      type: [String, Number],
      required: true
    },
    isQuantityControl: {
      type: Boolean,
      required: true
    },
    clientList: {
      type: Array,
      required: false
    },
    isBenepoExchange: {
      type: Boolean,
      required: true
    },
    saleEndDate: {
      type: String,
      required: false,
      default: ""
    },
    isHiddenItem: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  components: {
    FormCount,
    ButtonMain,
    InputSelect,
    IconBase,
    IconFavorite40px,
    IconCart,
    TheC01ItemFormNotePanelShortage,
    TheC01ItemFormNotePanelDrink,
    TheC01ItemFormNotePanelGiftCard,
    C01ItemFormExtraProp,
    ValidationErrorText,
    TheC01ItemFormOnlyTypeInCart
  },
  watch: {
    options(newVal: any[]) {
      if (newVal && newVal.length > 0 && this.optionTypes.length === 0) {
        this.skuId = newVal[0].id;
        newVal[0].options.forEach((ele: any) => {
          this.flag.push(ele.id.toString());
        });
      }
    }
  }
})
export default class TheC01ItemForm extends Vue {
  options!: any;
  optionTypes!: any;
  isFavorite!: any;
  point!: any;
  stock!: any;
  jan_code!: any;
  type!: any;
  extraProp!: any;
  cartItem!: any;
  possession!: any;
  creditCardEnabled!: any;
  ageAlert!: boolean;
  id!: number | string;
  isQuantityControl!: boolean;
  clientList!: any;
  isBenepoExchange!: boolean;
  saleEndDate!: string;
  isHiddenItem!: boolean;

  allOptions: any = [];
  size = "";
  color = "";
  count = 1;
  isAdult = false;
  extraPropValue = "";
  extraPropSecondValue = "";
  extraPropThirdValue = "";
  blankString = "";
  flag: any = [];
  cartItemId = 0;
  cartItemQty = 0;
  skuId = 0;
  clientListOption = "";
  itemOptions: any = [];
  form: any;

  schema = yup.object().shape({
    clientListOption: yup
      .string()
      .required("${label}を選択してください。")
      .label("お客様番号"),
    ...this.optionTypes.reduce((acc: any, option: any, index: number) => {
      acc[`option_${index}`] = yup
        .string()
        .required(`${option.name}を選択してください。`)
        .label(option.name);
      return acc;
    }, {}),
    ...this.extraProp.reduce((acc: any, option: any, index: number) => {
      acc[`extra_${index}`] = yup
        .string()
        .required(`${option.name}を入力してください。`)
        .matches(
          new RegExp(option.regex || ".*"),
          `${option.name}の形式が正しくありません`
        )
        .min(
          option.min_length,
          `${option.name}は${option.min_length}文字以上で入力してください。`
        )
        .max(
          option.max_length,
          `${option.name}は${option.max_length}文字以内で入力してください。`
        )
        .label(option.name);
      return acc;
    }, {})
  });

  get hasStock() {
    return (
      !this.isQuantityControl || (this.isQuantityControl && this.stock > 0)
    );
  }
  get haveExtraProps() {
    return this.extraProp.length > 0;
  }

  get isOnlyTypeInCart() {
    return this.cartItem.length > 0;
  }

  get maxStock() {
    return this.type == "web_coupon" ? 1 : this.stock;
  }

  get isGiftCardAndNormalItemInCart() {
    const isGiftCard = this.type === "gift_card";
    const hasNormalItemInCart = this.cartItem.some(
      (item: any) => item.sku_data.redemption_type === 1
    );
    return (
      isGiftCard && hasNormalItemInCart && this.stock > 0 && this.possession > 0
    );
  }

  get isWebCouponAndGiftItemInCart() {
    const isGiftCard = this.type === "web_coupon";
    const hasNormalItemInCart = this.cartItem.some(
      (item: any) => item.sku_data.redemption_type === 2
    );
    return (
      isGiftCard && hasNormalItemInCart && this.stock > 0 && this.possession > 0
    );
  }

  get isGiftCardAndWebCouponItemInCart() {
    const isGiftCard = this.type === "gift_card";
    const hasNormalItemInCart = this.cartItem.some(
      (item: any) => item.sku_data.redemption_type === 3
    );
    return (
      isGiftCard && hasNormalItemInCart && this.stock > 0 && this.possession > 0
    );
  }

  get isWebCouponAndNormalItemInCart() {
    const isGiftCard = this.type === "web_coupon";
    const hasNormalItemInCart = this.cartItem.some(
      (item: any) => item.sku_data.redemption_type === 1
    );
    return (
      isGiftCard && hasNormalItemInCart && this.stock > 0 && this.possession > 0
    );
  }

  get isNormalItemAndGiftCardInCart() {
    const isNormalItem = this.type === "normal";
    const hasGiftCardInCart = this.cartItem.some(
      (item: any) => item.sku_data.redemption_type !== 1
    );
    return (
      isNormalItem && hasGiftCardInCart && this.stock > 0 && this.possession > 0
    );
  }

  get isBenepoExchangeAndSomethingsInCart() {
    const isBenepoExchage = this.isBenepoExchange;
    const haveSomethingsInCart = this.cartItem.length !== 0;
    return isBenepoExchage && haveSomethingsInCart;
  }

  get isLiquor() {
    return this.type === true;
  }
  get isDrinkStop() {
    return this.ageAlert && !this.isAdult;
  }

  get isSkuSelected() {
    return this.skuId != 0;
  }
  get favoriteLabel() {
    return this.isFavorite ? "お気に入りから外す" : "お気に入りに追加";
  }
  get isShortage() {
    const total = this.point * this.count;
    const shortage = this.possession > 0 && this.possession < total;
    return this.hasStock && shortage;
  }

  get productRequiredpoints() {
    return this.point * this.count;
  }

  get isCreditCardEnabledAndItemInCart() {
    const creditCardEnabled = this.creditCardEnabled;
    const cartItemCount = this.cartItem.length > 0;
    const count = this.count != 1;
    return creditCardEnabled && (cartItemCount || count);
  }

  get cartEmpty() {
    return this.cartItem.length == 0;
  }

  get userPointsAreGreaterThanProductPoint() {
    const userPoints = this.possession;
    const productRequiredpoints = this.productRequiredpoints;
    return userPoints >= productRequiredpoints;
  }

  get compareSaleEndDate() {
    if (this.saleEndDate == "") {
      return true;
    }
    const endDate = new Date(this.saleEndDate);
    const today = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    return endDate >= today;
  }

  get disabled() {
    if (this.skuId == 0) {
      return true;
    }
    if (this.isOnlyTypeInCart) {
      return true;
    }
    if (this.hasStock) {
      if (!this.isQuantityControl || this.stock >= this.count) {
        if (this.isDrinkStop) {
          return true;
        }
        if (this.possession >= this.productRequiredpoints) {
          //credit card not required
          if (this.cartItem.length > 0) {
            if (
              this.isBenepoExchangeAndSomethingsInCart ||
              this.isGiftCardAndNormalItemInCart ||
              this.isNormalItemAndGiftCardInCart ||
              this.isWebCouponAndGiftItemInCart ||
              this.isGiftCardAndWebCouponItemInCart ||
              this.isWebCouponAndNormalItemInCart
            ) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        } else {
          if (this.possession < 1) {
            return true;
          }
          //credit required
          if (this.creditCardEnabled) {
            if (this.cartItem.length > 0 || this.count != 1) {
              // console.log("credit card not allowed because cart is not empty or count is not equal to 1 ");
              return true;
            } else {
              if (this.type !== "gift_card") {
                return false;
              } else {
                return true;
              }
            }
          } else {
            return true;
          }
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  favorite() {
    this.$emit("favorite", this.skuId);
  }
  unfavorite() {
    this.$emit("unfavorite", this.skuId);
  }
  changeFavorite() {
    this.isFavorite ? this.unfavorite() : this.favorite();
  }
  onCheckAdult() {
    this.isAdult = !this.isAdult;
  }

  getCartItemCount() {
    const cartItems = this.getCartItems();
    return cartItems.length;
  }

  getCartItems() {
    return JSON.parse(LocalStorage.getItem("cartDetails") as string);
  }

  getUserDetails() {
    return JSON.parse(LocalStorage.getItem("userDetails") as string);
  }

  submit(ObserverProps: any) {
    ObserverProps.passes(async () => {
      const userDetails = this.getUserDetails();

      const sku = this.options.find((element: any) => {
        return element.options.every(
          (e: any, i: any) => e.value == this.flag[i]
        );
      });

      if (sku !== undefined) {
        this.skuId = sku.id;
      } else {
        window.alert("オプションは利用できません");
        return;
      }

      const browserHistoryRequest: BrowsingHistoryRequest = {
        user: userDetails.id,
        sku: this.skuId
      };
      await postBrowserHistory(browserHistoryRequest);

      const cartItems = await getShoppingCartItems(1, 90, "");

      const itemExist = cartItems?.results?.find((item: any) => {
        return item.sku_id == this.skuId;
      });

      if (itemExist !== undefined) {
        this.cartItemId = itemExist.id;
        this.cartItemQty = itemExist.quantity;

        const PatchedShoppingCartItemRequest: PatchedShoppingCartItemRequest = {
          user: userDetails.id,
          sku_id: this.skuId,
          quantity: this.count + this.cartItemQty,
          option_type_extra: this.sortedExtraOptions,
          customer_no:
            this.clientListOption.length > 0
              ? this.clientListOption.split(",")[0]
              : "",
          torino_no:
            this.clientListOption.length > 0
              ? this.clientListOption.split(",")[1]
              : ""
        };

        const res = await patchItemfromShoppingcart(
          this.cartItemId,
          PatchedShoppingCartItemRequest
        ).catch(async (error: any) => {
          alert(error.response.data);
          window.location.reload();
        });

        if (res) {
          this.form.submit();
        }
      } else {
        const shoppingCartItemRequest: ShoppingCartItemRequest = {
          user: userDetails.id,
          sku_id: this.skuId,
          quantity: this.count,
          option_type_extra: this.itemOptions.concat(this.sortedExtraOptions),
          customer_no:
            this.clientListOption.length > 0
              ? this.clientListOption.split(",")[0]
              : "",
          torino_no:
            this.clientListOption.length > 0
              ? this.clientListOption.split(",")[1]
              : ""
        };
        const res = await postShoppingCartItems(shoppingCartItemRequest).catch(
          (error: any) => {
            alert(error.response.data);
            window.location.reload();
          }
        );

        if (res) {
          this.form.submit();
        }
      }
    });
  }

  getOptions(id: number) {
    const allOptions: any = [];
    const checkDuplicateValues: any = [];
    this.options.forEach((item: any) => {
      item.options.forEach((ele: any) => {
        if (ele.option_type == id) {
          if (checkDuplicateValues.indexOf(ele.value) == -1) {
            allOptions.push({ label: ele.value, value: ele.value });
            checkDuplicateValues.push(ele.value);
          }
        }
      });
    });
    return allOptions;
  }

  onChangeSelect(key: any, optionId: number, optionName: any) {
    const skuId = this.options[0].id;
    if (this.itemOptions.length === 0) {
      this.itemOptions.push({
        id: optionId,
        sku: skuId !== undefined ? skuId : "-",
        name: optionName,
        value: key
      });
    } else if (this.itemOptions.length !== this.optionTypes.length) {
      this.itemOptions.forEach(
        (itemOption: { id: number; sku: any; name: any; value: any }) => {
          if (itemOption.id === optionId) {
            itemOption.value = key;
          } else if (itemOption.id !== optionId) {
            this.itemOptions = this.itemOptions.concat([
              {
                id: optionId,
                sku: skuId !== undefined ? skuId : "-",
                name: optionName,
                value: key
              }
            ]);
          }
        }
      );
    } else if (this.itemOptions.length === this.optionTypes.length) {
      this.itemOptions = this.itemOptions.map(
        (itemOption: { id: number; sku: any; name: any; value: any }) =>
          itemOption.id === optionId
            ? {
                id: optionId,
                sku: skuId !== undefined ? skuId : "-",
                name: optionName,
                value: key
              }
            : itemOption
      );
    }
    if (this.flag.length == this.optionTypes.length) {
      const sku = this.options.find((element: any) => {
        return element.options.every(
          (e: any, i: any) => e.value == this.flag[i]
        );
      });
      if (sku !== undefined) {
        this.skuId = sku.id;
        this.$emit("update:point", sku.point_required);
        this.$emit(
          "update:stock",
          sku.is_quantity_control ? sku.quantity : 9999
        );
        this.$emit("update:is-quantity-control", sku.is_quantity_control);
        this.$emit("update:jan_code", sku.jan_code);
        this.$emit("update:isFavorite", sku.is_favorite);
        this.$emit("update:extra-prop", sku.optiontypeextra_set);

        if (
          sku.optiontypeextra_set &&
          sku.optiontypeextra_set.length > 0 &&
          sku.optiontypeextra_set[0].name ==
            "私は20歳以上です（はいorいいえ）" &&
          sku.optiontypeextra_set[0].regex == "^はい$"
        ) {
          this.$emit("update:age-alert", true);
        } else {
          this.$emit("update:age-alert", false);
        }
      } else {
        this.skuId = 0;
      }
    }
  }

  sortByKey(array: any, key: any) {
    return array.sort(function(a: any, b: any) {
      const x = a[key];
      const y = b[key];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  }

  get sortedExtraOptions() {
    return this.sortByKey(this.extraProp, "display_order").map((ele: any) => {
      return {
        ...ele,
        value: "",
        sku: ele.sku.toString(),
        regex: ele.regex != null ? ele.regex : ""
      };
    });
  }

  mounted() {
    const formElement = this.$refs.form as HTMLFormElement;
  }
}
</script>

<style lang="scss" scoped>
.p-the-c01-form {
  &__item {
    display: flex;
    align-items: center;
    margin-top: rem(16);

    &:first-of-type {
      margin-top: rem(24);
    }

    &__label {
      @include text__body--s;
      width: rem(56);
      margin-left: rem(8);
    }

    &__select {
      width: rem(120);
    }
  }

  &__extras {
    &__item {
      padding: rem(24) rem(16) 0;
    }
  }

  &__button_group {
    display: flex;
    flex-direction: row;
    margin-top: rem(40);

    @include large-sp {
      flex-direction: column;
    }

    @include sp {
      align-items: center;
      margin-top: rem(24);
    }

    &__item {
      &:first-of-type {
        @include large-pc {
          margin-right: rem(16);
        }

        @include large-sp {
          margin-bottom: rem(24);
        }
      }
    }
  }

  &__note {
    margin-top: rem(40);

    @include sp {
      margin-top: rem(32);
      display: flex;
      justify-content: center;
    }
  }
}
</style>
